import { getDefaultNodeImage, getNodeDescription, getNodeSocial, getNodeUrl } from "Areas/SharedFeatures";
import getShorterText from "Common/componentUtils/getShorterText";
import { FORMAT, formatDateTime } from "Common/dateTimeUtils/format";
import { DOCUMENT_CONTENT_TYPE } from "Common/dictionaries/documentContentType";
import Button from "Components/button";
import MediaPlayer from "Components/mediaPlayer";
import Menu from "Components/menu";
import ProgressOverlay from "Components/progressOverlay";
import { Tab, TabPanel, Tabs } from "Components/tabs";
import { themeVariables } from "Components/theme";
import classNames from "classnames";
import groupBy from "lodash/groupBy";
import head from "lodash/head";
import map from "lodash/map";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NODE_GROUP_ID_ATTR, NODE_ID_ATTR } from "../../constants";
import buildOnContextMenuCallback from "../utils/contextMenu/buildOnContextMenuCallback";
import Author from "./author";
import BlankPanel from "./blankPanel";
import locale from "./post.locale";
const SIZES = {
    MAX_TEXT_SIZE: 300,
    LOAD_BUTTON_HEIGHT: 28,
    AUTHOR_AVATAR_SIZE: 30,
    TAB_TITLE_BADGE_HEIGHT: 20,
    IMAGE_STYLE: { maxWidth: "50vw", height: "33vh" },
    POST_WIDTH: 869
};
const documentContentTypeToComponent = {
    [DOCUMENT_CONTENT_TYPE.TEXT]: ({ value, className, style }) => (React.createElement("div", { className: className, style: style }, value)),
    [DOCUMENT_CONTENT_TYPE.IMAGE]: ({ value, className, style, forwardedRef }) => (React.createElement("img", { className: className, src: value, style: style, ref: forwardedRef })),
    [DOCUMENT_CONTENT_TYPE.AUDIO]: ({ value, className, style }) => (React.createElement("div", { className: className, style: style },
        React.createElement(MediaPlayer, { type: "audio", source: value }))),
    [DOCUMENT_CONTENT_TYPE.VIDEO]: ({ value, className, style }) => (React.createElement("div", { className: className, style: style },
        React.createElement(MediaPlayer, { type: "video", source: value })))
};
const postHeaderStyle = { padding: "13px 2em 13px 2.5em" };
const postIconStyle = { width: "1.2em", height: "1.2em", left: "0.8em" };
const commentTextStyle = { paddingLeft: SIZES.AUTHOR_AVATAR_SIZE };
const tabBadgeStyle = { height: `${SIZES.TAB_TITLE_BADGE_HEIGHT}px` };
export default function Post({ nodeSource, documentObjects, onLoadContent, isSingle, onLoadComments, onLoadLikes, masterNodeSource, className, documentId, socialComments, socialLikes, waitingComments, waitingLikes, onGetVideoUrl, onGetAudioUrl, onPhotoSendToGraph, onTextSendToGraph, blockIdPrefix }) {
    const [inProgress, setInProgress] = useState(false);
    const [tab, setTab] = useState(0);
    const [text, setText] = useState("");
    const [url, setUrl] = useState();
    const [image, setImage] = useState("");
    const [audio, setAudio] = useState("");
    const [video, setVideo] = useState("");
    const [isWaitingComments, setIsWaitingComments] = useState(false);
    const [isWaitingLikes, setIsWaitingLikes] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [truncateText, setTruncateText] = useState(true);
    const [comments, setComments] = useState([]);
    const [likes, setLikes] = useState([]);
    const [social, setSocial] = useState({ commentsCount: 0, likesCount: 0 });
    const postBlockId = useMemo(() => `${blockIdPrefix}${nodeSource.NodeId}`, [nodeSource.NodeId]);
    const postBlockAttrs = useMemo(() => ({
        [NODE_ID_ATTR]: nodeSource.NodeId,
        [NODE_GROUP_ID_ATTR]: nodeSource.GroupId
    }), [nodeSource.NodeId, nodeSource.GroupId]);
    const contextMenuRef = useRef(null);
    const imageComponentRef = useRef(null);
    useEffect(() => {
        const documentObject = documentObjects
            ? documentObjects[nodeSource.DocumentObjectId]
            : undefined;
        setText(getNodeDescription(nodeSource, documentObject));
        setUrl(getNodeUrl(nodeSource, documentObject));
        setSocial(getNodeSocial(nodeSource, documentObject));
    }, []);
    useEffect(() => {
        waitingComments &&
            waitingComments[nodeSource.NodeId] !== undefined &&
            setIsWaitingComments(waitingComments[nodeSource.NodeId]);
    }, [waitingComments, nodeSource]);
    useEffect(() => {
        waitingLikes &&
            waitingLikes[nodeSource.NodeId] !== undefined &&
            setIsWaitingLikes(waitingLikes[nodeSource.NodeId]);
    }, [waitingLikes, nodeSource]);
    useEffect(() => {
        socialComments &&
            socialComments[nodeSource.NodeId] &&
            setComments(socialComments[nodeSource.NodeId]);
    }, [socialComments, nodeSource]);
    useEffect(() => {
        socialLikes && socialLikes[nodeSource.NodeId] && setLikes(socialLikes[nodeSource.NodeId]);
    }, [socialLikes, nodeSource]);
    const handleLoadComments = useCallback(() => {
        onLoadComments && onLoadComments(nodeSource.NodeId, documentId);
    }, [onLoadComments, nodeSource, documentId]);
    const handleLoadLikes = useCallback(() => {
        onLoadLikes && onLoadLikes(nodeSource.NodeId, documentId);
    }, [onLoadLikes, nodeSource, documentId]);
    const documentObject = documentObjects
        ? documentObjects[nodeSource.DocumentObjectId]
        : undefined;
    const title = useMemo(() => {
        if (!documentObject)
            return undefined;
        return `${documentObject.Name} - ${formatDateTime(nodeSource.Created, FORMAT.DATE_TIME)}`;
    }, [nodeSource, documentObject]);
    const authorDefaultTitle = useMemo(() => { var _a; return (_a = documentObject === null || documentObject === void 0 ? void 0 : documentObject.Name) !== null && _a !== void 0 ? _a : title; }, [title]);
    const ComponentText = documentContentTypeToComponent[DOCUMENT_CONTENT_TYPE.TEXT];
    const ComponentImage = documentContentTypeToComponent[DOCUMENT_CONTENT_TYPE.IMAGE];
    const ComponentAudio = documentContentTypeToComponent[DOCUMENT_CONTENT_TYPE.AUDIO];
    const ComponentVideo = documentContentTypeToComponent[DOCUMENT_CONTENT_TYPE.VIDEO];
    const imageBlock = useMemo(() => image ? (React.createElement(ComponentImage, { value: image, style: SIZES.IMAGE_STYLE, forwardedRef: imageComponentRef })) : (React.createElement("div", null)), [image]);
    const audioBlock = useMemo(() => (audio ? React.createElement(ComponentAudio, { value: audio }) : React.createElement("div", null)), [audio]);
    const videoBlock = useMemo(() => (video ? React.createElement(ComponentVideo, { value: video }) : React.createElement("div", null)), [video]);
    const defaultNodeImageSrc = documentObjects
        ? getDefaultNodeImage(documentObjects[nodeSource.DocumentObjectId])
        : undefined;
    const renderedComments = useMemo(() => comments && (React.createElement("div", null, comments.map((comment, index) => (React.createElement("div", { key: comment.Id },
        index !== 0 && React.createElement("hr", { className: "border-top-gray-100 mt-1 mb-1 ml-2" }),
        React.createElement("div", null,
            React.createElement(Author, { data: comment, defaultSrc: defaultNodeImageSrc, defaultTitle: authorDefaultTitle, imgSize: SIZES.AUTHOR_AVATAR_SIZE }),
            React.createElement(ComponentText, { value: comment.Text, className: "pre-wrap break-word ml-0_5 mt-0_5 line-height-medium", style: commentTextStyle }))))))), [comments, defaultNodeImageSrc, title, authorDefaultTitle]);
    const renderedLikes = useMemo(() => likes && (React.createElement("div", null, likes.map((like, index) => (React.createElement("div", { key: like.Id },
        index !== 0 && React.createElement("hr", { className: "border-top-gray-100 mt-1 mb-1 ml-2" }),
        React.createElement("div", null,
            React.createElement(Author, { data: like, defaultSrc: defaultNodeImageSrc, defaultTitle: authorDefaultTitle, imgSize: SIZES.AUTHOR_AVATAR_SIZE }))))))), [likes, authorDefaultTitle]);
    const getTabTitleWithBadge = useCallback((caption, length) => (React.createElement("div", { className: "flex-single-row with-gap-4" },
        caption,
        React.createElement("div", { className: "bg-layers-primary-opacity-12  text-light-on-surface-variant p-2-6 border-rounded-small font-small line-height-base", style: tabBadgeStyle }, length))), []);
    const tabs = useMemo(() => [
        {
            id: 1,
            title: getTabTitleWithBadge(locale.TAB_COMMENTS, social.commentsCount),
            render: () => (React.createElement(ProgressOverlay, { show: isWaitingComments }, comments.length === 0 ? (React.createElement(BlankPanel, { button: React.createElement(Button, { className: "", height: SIZES.LOAD_BUTTON_HEIGHT, onClick: handleLoadComments },
                    React.createElement("i", { className: "icon-comments font-h5 mr-0_5" }),
                    locale.LOAD_COMMENTS) })) : (renderedComments)))
        },
        {
            id: 2,
            title: getTabTitleWithBadge(locale.TAB_LIKES, social.likesCount),
            render: () => (React.createElement(ProgressOverlay, { show: isWaitingLikes }, likes.length === 0 ? (React.createElement(BlankPanel, { button: React.createElement(Button, { className: "", height: SIZES.LOAD_BUTTON_HEIGHT, onClick: handleLoadLikes },
                    React.createElement("i", { className: "icon-like font-h5 mr-0_5" }),
                    locale.LOAD_LIKES) })) : (renderedLikes)))
        }
    ], [
        renderedComments,
        renderedLikes,
        handleLoadComments,
        handleLoadLikes,
        isWaitingComments,
        isWaitingLikes,
        social
    ]);
    const handleLoadImage = useCallback(() => {
        if (!image && !imageLoaded && onLoadContent && masterNodeSource) {
            setInProgress(true);
            onLoadContent(nodeSource.NodeId, masterNodeSource.NodeId, (loadedNodeSource) => {
                setInProgress(false);
                const contentGroups = groupBy(loadedNodeSource.DocumentContents, "DocumentContentType");
                const contentImages = contentGroups[DOCUMENT_CONTENT_TYPE.IMAGE];
                const contentAudios = contentGroups[DOCUMENT_CONTENT_TYPE.AUDIO];
                const contentVideos = contentGroups[DOCUMENT_CONTENT_TYPE.VIDEO];
                const headImage = head(contentImages);
                if (headImage === null || headImage === void 0 ? void 0 : headImage.Value) {
                    setImage(headImage.Value);
                }
                const headVideo = head(contentVideos);
                if (headVideo) {
                    onGetVideoUrl && setVideo(onGetVideoUrl(headVideo));
                }
                const headAudio = head(contentAudios);
                if (headAudio) {
                    onGetAudioUrl && setAudio(onGetAudioUrl(headAudio));
                }
                setImageLoaded(true);
            }, () => {
                setInProgress(false);
                setImage(".");
            });
        }
    }, [image, video, audio, imageLoaded, nodeSource, onGetVideoUrl]);
    const handleContextMenu = useCallback(buildOnContextMenuCallback(documentId, documentObjects, nodeSource, contextMenuRef, imageComponentRef, onPhotoSendToGraph, onTextSendToGraph), [documentId, documentObjects, nodeSource, onPhotoSendToGraph, onTextSendToGraph]);
    useEffect(() => {
        isSingle && handleLoadImage();
    }, [image, isSingle]);
    const handleTruncateClick = useCallback(() => setTruncateText(!truncateText), [truncateText]);
    if (!text || !masterNodeSource) {
        return React.createElement("div", null);
    }
    return (React.createElement("div", { className: classNames("border-rounded-base border overflow-hidden", className), onMouseEnter: handleLoadImage, style: { width: SIZES.POST_WIDTH } },
        React.createElement(ProgressOverlay, { show: inProgress },
            React.createElement("div", { onContextMenu: handleContextMenu, id: postBlockId, ...postBlockAttrs },
                React.createElement("div", { className: "w-100 bg-layers-primary-opacity-008 line-height-small position relative", style: postHeaderStyle },
                    React.createElement("img", { src: defaultNodeImageSrc, className: "position-absolute", style: postIconStyle }),
                    title),
                React.createElement("div", { className: "p-xs bg-white" },
                    React.createElement(Author, { data: nodeSource, defaultSrc: defaultNodeImageSrc, defaultTitle: authorDefaultTitle, imgSize: SIZES.AUTHOR_AVATAR_SIZE, url: url }),
                    React.createElement("div", { className: "w-100 mt-1 line-height-medium" },
                        React.createElement(ComponentText, { value: truncateText ? getShorterText(text, SIZES.MAX_TEXT_SIZE) || text : text, className: "pre-wrap break-word" }),
                        text.length > SIZES.MAX_TEXT_SIZE && (React.createElement("div", { className: "mt-0_5 text-primary-600 clickable", onClick: handleTruncateClick }, truncateText ? locale.TRUNCATE_SHOW : locale.TRUNCATE_COLLAPSE))),
                    React.createElement("div", { className: "w-100 mt-1" }, imageBlock),
                    React.createElement("div", { className: "w-100 mt-1" }, audioBlock),
                    React.createElement("div", { className: "w-100 mt-1" }, videoBlock),
                    React.createElement(Tabs, { onTabChange: setTab, selectedTabIndex: tab }, map(tabs, item => [
                        React.createElement(Tab, { title: item.title, key: `tab-${item.id}` }),
                        React.createElement(TabPanel, { key: `tab-panel-${item.id}` }, item.render ? item.render() : React.createElement("div", null))
                    ]))),
                React.createElement(Menu, { mode: "contextmenu", zIndex: parseInt(themeVariables["zindex-pageheader-menu"]) + 1, menuRef: contextMenuRef })))));
}
